import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Play, Pause, SkipForward, SkipBack, Volume2, X } from "lucide-react";
import { IconMusic } from "@/components/HeaderIcons";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Module-level singleton — the audio lives OUTSIDE React so it never stops
// when components remount, routes change, or the panel is closed.
const eng = {
  audio: new Audio(),
  tracks: [],
  playlist: [],
  idx: 0,
};
eng.audio.preload = "auto";
eng.fader = new Audio();
{
  const v = parseFloat(localStorage.getItem("pb_music_vol"));
  eng.vol = isNaN(v) ? 0.5 : v;
  eng.audio.volume = eng.vol;
}

const matchesPage = (t, path) =>
  (t.pages || []).some((p) => (p === "/" ? path === "/" || path.startsWith("/profile") : path === p || path.startsWith(p + "/")));

function playlistFor(path) {
  const pageList = eng.tracks.filter((t) => matchesPage(t, path));
  if (pageList.length) return { list: pageList, scoped: true };
  return { list: eng.tracks.filter((t) => !(t.pages || []).length), scoped: false };
}

function startSrc(url, startAt = 0) {
  localStorage.setItem("pb_music_fname", url);
  eng.audio.src = `${BACKEND}${url}`;
  if (startAt > 0) {
    const seek = () => { eng.audio.currentTime = startAt; eng.audio.removeEventListener("loadedmetadata", seek); };
    eng.audio.addEventListener("loadedmetadata", seek);
  }
  eng.audio.play().catch(() => {
    // browser blocked autoplay — start on the first interaction instead
    const once = () => { document.removeEventListener("pointerdown", once); eng.audio.play().catch(() => {}); };
    document.addEventListener("pointerdown", once);
  });
}

function playTrack(i, startAt = 0) {
  if (!eng.playlist.length) return;
  const n = ((i % eng.playlist.length) + eng.playlist.length) % eng.playlist.length;
  eng.idx = n;
  startSrc(eng.playlist[n].url, startAt);
}

const savedPos = () => parseFloat(localStorage.getItem("pb_music_pos")) || 0;
eng.audio.addEventListener("timeupdate", () => {
  if (!eng.audio.paused) localStorage.setItem("pb_music_pos", eng.audio.currentTime);
});
eng.audio.addEventListener("play", () => localStorage.setItem("pb_music_on", "1"));
eng.audio.addEventListener("pause", () => {
  if (!eng.audio.ended) localStorage.setItem("pb_music_on", "0");
});
eng.audio.addEventListener("ended", () => {
  localStorage.setItem("pb_music_pos", "0");
  playTrack(eng.idx + 1);
});

// ---- Crossfade: the outgoing song fades into the next like a live DJ set ----
const FADE_S = 3.5;
eng.fading = false;
eng.audio.addEventListener("timeupdate", () => {
  const a = eng.audio;
  if (eng.fading || a.paused || !eng.playlist.length) return;
  if (!isFinite(a.duration) || a.duration < FADE_S * 2) return;
  const remaining = a.duration - a.currentTime;
  if (remaining > FADE_S || remaining <= 0.1) return;
  eng.fading = true;
  const f = eng.fader;
  const handoff = (overlap) => {
    a.volume = overlap ? 0 : eng.vol;
    playTrack(eng.idx + 1);
    const started = Date.now();
    const iv = setInterval(() => {
      const p = Math.min(1, (Date.now() - started) / (FADE_S * 1000));
      if (a.paused || p >= 1) {
        clearInterval(iv);
        f.pause();
        f.removeAttribute("src");
        a.volume = eng.vol;
        eng.fading = false;
        return;
      }
      a.volume = eng.vol * Math.sin((p * Math.PI) / 2);
      f.volume = eng.vol * Math.cos((p * Math.PI) / 2);
    }, 60);
  };
  let done = false;
  const fallback = setTimeout(() => { if (!done) { done = true; handoff(false); } }, 900);
  f.src = a.src;
  f.volume = a.volume;
  f.addEventListener("loadedmetadata", () => {
    if (done) return;
    done = true;
    clearTimeout(fallback);
    f.currentTime = a.currentTime;
    f.play().then(() => handoff(true)).catch(() => { f.pause(); handoff(false); });
  }, { once: true });
});
eng.audio.addEventListener("pause", () => {
  if (eng.fading) { try { eng.fader.pause(); } catch { /* noop */ } }
});
// stream died mid-song (network hiccup / server blip) — resume where it stopped
eng.retries = 0;
eng.audio.addEventListener("timeupdate", () => { eng.retries = 0; });
eng.audio.addEventListener("error", () => {
  if (!eng.audio.src || eng.retries >= 3) return;
  eng.retries += 1;
  const src = eng.audio.src;
  const pos = savedPos();
  setTimeout(() => {
    eng.audio.src = src;
    const seek = () => { eng.audio.currentTime = pos; eng.audio.removeEventListener("loadedmetadata", seek); };
    eng.audio.addEventListener("loadedmetadata", seek);
    eng.audio.play().catch(() => {});
  }, 1200 * eng.retries);
});

const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export default function MusicPlayer({ variant, onAction }) {
  const [playlist, setPlaylist] = useState(eng.playlist);
  const [scoped, setScoped] = useState(false);
  const [idx, setIdx] = useState(eng.idx);
  const [playing, setPlaying] = useState(!eng.audio.paused);
  const [open, setOpen] = useState(false);
  const [volume, setVolume] = useState(eng.vol);
  const [time, setTime] = useState(eng.audio.currentTime || 0);
  const [dur, setDur] = useState(eng.audio.duration || 0);
  const location = useLocation();

  useEffect(() => {
    api.get("/music/tracks").then(({ data }) => { eng.tracks = data; syncPlaylist(true); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncPlaylist = (initial = false) => {
    const path = window.location.pathname;
    const { list, scoped: sc } = playlistFor(path);
    const current = eng.playlist[eng.idx];
    eng.playlist = list;
    setScoped(sc);
    const keep = current ? list.findIndex((t) => t.url === current.url) : -1;
    eng.idx = keep >= 0 ? keep : -1;
    setIdx(eng.idx);
    setPlaylist(list);
    if (!list.length) return;
    // A playing song is NEVER cut — the page's playlist simply takes over once it ends.
    if (!eng.audio.paused && eng.audio.src) return;
    // reload mid-listen ("1"): the resume effect brings the same song back where it left off
    // paused by the player ("0"): stay silent — they're in control
    const on = localStorage.getItem("pb_music_on");
    if (sc && !eng.audio.src && on !== "0" && on !== "1") playTrack(0);
  };

  // page changed → swap to that page's soundtrack
  useEffect(() => {
    if (eng.tracks.length) syncPlaylist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const a = eng.audio;
    const onPlay = () => { setPlaying(true); setIdx(eng.idx); };
    const onPause = () => setPlaying(false);
    const onTime = () => setTime(a.currentTime);
    const onDur = () => setDur(a.duration || 0);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    return () => {
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
    };
  }, []);

  // resume where the song left off if the player closed out while music was on
  useEffect(() => {
    if (!playlist.length || eng.audio.src || localStorage.getItem("pb_music_on") !== "1") return;
    const kick = () => {
      document.removeEventListener("pointerdown", kick);
      document.removeEventListener("keydown", kick);
      if (eng.audio.src || !eng.playlist.length) return;
      const savedUrl = localStorage.getItem("pb_music_fname");
      const i = eng.playlist.findIndex((t) => t.url === savedUrl);
      if (i >= 0) playTrack(i, savedPos());
      else {
        // saved song isn't in this page's playlist — still finish it, playlist takes over after
        const t = eng.tracks.find((x) => x.url === savedUrl);
        if (t) { eng.idx = -1; startSrc(t.url, savedPos()); }
        else playTrack(0);
      }
    };
    document.addEventListener("pointerdown", kick);
    document.addEventListener("keydown", kick);
    return () => { document.removeEventListener("pointerdown", kick); document.removeEventListener("keydown", kick); };
  }, [playlist]);

  const toggle = () => {
    const a = eng.audio;
    if (playing) a.pause();
    else if (!a.src) {
      const savedUrl = localStorage.getItem("pb_music_fname");
      const i = eng.playlist.findIndex((t) => t.url === savedUrl);
      if (i >= 0) playTrack(i, savedPos()); else playTrack(eng.idx);
    } else a.play().catch(() => {});
  };

  const setVol = (v) => {
    eng.vol = v;
    if (!eng.fading) eng.audio.volume = v;
    setVolume(v);
    localStorage.setItem("pb_music_vol", v);
  };

  if (!eng.tracks.length || !playlist.length) return null;

  return (
    <>
      {variant === "menu" ? (
        <button data-testid="music-toggle-btn" onClick={() => { setOpen(true); onAction?.(); }} className="hm-item">
          <span className="relative inline-flex">
            <IconMusic size={24} />
            {playing && <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#A3E635] animate-pulse" />}
          </span>
          <span>Music{playing ? " · Playing" : ""}</span>
        </button>
      ) : (
      <button data-testid="music-toggle-btn" onClick={() => setOpen((o) => !o)} title="Bullpen soundtrack"
        className={`hdr-icon relative w-9 h-9 flex items-center justify-center ${playing ? "scale-105" : ""}`}
        style={playing ? { filter: "drop-shadow(0 0 10px rgba(163,230,53,.7))" } : undefined}>
        <IconMusic size={26} />
        {playing && <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#A3E635] animate-pulse" />}
      </button>
      )}
      {open && createPortal(
        <div className="fixed inset-0 z-[70]" onClick={() => setOpen(false)} data-testid="music-overlay">
          <div className="absolute top-16 right-3 w-[min(92vw,320px)] bg-pb-surface border border-pb-border shadow-[0_12px_40px_rgba(0,0,0,0.6)] animate-fade-up"
            onClick={(e) => e.stopPropagation()} data-testid="music-panel">
            <div className="flex items-start justify-between gap-2 p-3 border-b border-pb-border/60">
              <div className="min-w-0">
                <div className="text-[8px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-1" data-testid="music-playlist-label">
                  {scoped ? "This Page's Soundtrack" : "Bullpen Soundtrack"}
                </div>
                <div className="text-xs font-bold text-white truncate" data-testid="music-now-playing">{playlist[idx]?.title || eng.tracks.find((t) => `${BACKEND}${t.url}` === eng.audio.src)?.title}</div>
              </div>
              <button data-testid="music-close-btn" onClick={() => setOpen(false)} title="Close — music keeps playing"
                className="shrink-0 text-zinc-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-3 pt-3">
              <input data-testid="music-seek" type="range" min="0" max={dur || 0} step="0.1" value={Math.min(time, dur || 0)}
                onChange={(e) => { eng.audio.currentTime = parseFloat(e.target.value); setTime(parseFloat(e.target.value)); }}
                className="w-full accent-[#A3E635] h-1" />
              <div className="flex justify-between text-[9px] font-mono text-zinc-500">
                <span data-testid="music-time">{fmt(time)}</span>
                <span>{fmt(dur)}</span>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 py-2.5">
              <button data-testid="music-prev-btn" onClick={() => playTrack(eng.idx - 1)} className="text-zinc-400 hover:text-white transition-colors"><SkipBack className="w-4 h-4" /></button>
              <button data-testid="music-play-btn" onClick={toggle}
                className="w-10 h-10 flex items-center justify-center bg-[#A3E635] text-black hover:bg-[#BEF264] active:scale-95 transition-all">
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>
              <button data-testid="music-next-btn" onClick={() => playTrack(eng.idx + 1)} className="text-zinc-400 hover:text-white transition-colors"><SkipForward className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-2 px-3 pb-2.5">
              <Volume2 className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
              <input data-testid="music-volume" type="range" min="0" max="1" step="0.05" value={volume}
                onChange={(e) => setVol(parseFloat(e.target.value))} className="w-full accent-[#A3E635] h-1" />
            </div>
            <div className="max-h-40 overflow-y-auto border-t border-pb-border/60" data-testid="music-tracklist">
              {playlist.map((t, i) => (
                <button key={t.id} data-testid={`music-track-${t.id}`} onClick={() => playTrack(i)}
                  className={`w-full text-left px-3 py-1.5 text-[11px] truncate transition-colors ${i === idx ? "text-[#A3E635] bg-[#A3E635]/5" : "text-zinc-400 hover:text-white hover:bg-pb-elevated"}`}>
                  {i === idx && playing ? "▶ " : ""}{i + 1}. {t.title}
                </button>
              ))}
            </div>
            <div className="border-t border-pb-border/60 px-3 py-2 text-[9px] uppercase tracking-widest text-zinc-600 text-center">
              Closing this box won't stop the music — hit pause to stop
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
